import { fmtMoney, fmtDate } from "@/lib/format";
import { relationLabel } from "@/lib/india";

// Escape user-controlled strings before injecting them into the receipt HTML.
// All free-form fields (customer name, address, notes, vehicle data, payment
// notes, etc.) flow through this so the receipt window can't be hijacked via
// an attacker-controlled name or note containing markup or <script> tags.
function esc(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// A dialog-free, print-only receipt that opens in a new window.
// Avoids polluting the SPA with print CSS overrides.
export function printBookingReceipt({ booking, customer, dealershipName = "MotorLedger" }) {
  const vehicle = booking.vehicle || {};
  const payments = booking.payments || [];
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const total = Number(booking.total_price || 0);
  const balance = Math.max(total - totalPaid, 0);
  const relation =
    customer.relation_type
      ? `${relationLabel(customer.relation_type)} ${customer.relation_name || ""}`.trim()
      : "—";

  // Address: prefer composed/free-form, fall back to structured parts.
  const addrLines = customer.address
    ? customer.address.split(",").map((s) => s.trim()).filter(Boolean)
    : [
        customer.house_no,
        customer.street,
        customer.town_village,
        [customer.mandal, customer.district].filter(Boolean).join(", "),
        [customer.state, customer.pin_code].filter(Boolean).join(" — "),
        customer.country,
      ].filter(Boolean);

  const html = `
<!doctype html>
<html><head><meta charset="utf-8" />
<title>Booking ${esc(booking.id.slice(0, 8))}</title>
<style>
  * { box-sizing: border-box; font-family: 'IBM Plex Sans', -apple-system, sans-serif; }
  body { margin: 0; padding: 28px; color: #0A0A0A; background: #fff; }
  h1, h2, h3 { margin: 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0A0A0A; padding-bottom: 12px; margin-bottom: 18px; }
  .brand { font-size: 22px; font-weight: 800; letter-spacing: -.5px; }
  .eyebrow { font-family: 'IBM Plex Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 3px; color: #5C5C5C; }
  .meta { text-align: right; font-family: 'IBM Plex Mono', monospace; font-size: 11px; color: #5C5C5C; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 18px; }
  .card { border: 1px solid #E5E5E5; padding: 14px; }
  .card h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 3px; color: #5C5C5C; margin-bottom: 8px; }
  .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
  .row + .row { border-top: 1px dashed #E5E5E5; }
  .row .l { color: #5C5C5C; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 13px; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #E5E5E5; }
  th { font-family: 'IBM Plex Mono', monospace; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #5C5C5C; background: #FAFAFA; }
  .totals { margin-top: 14px; }
  .totals .row { font-size: 14px; }
  .balance { font-weight: 800; color: #002FA7; }
  .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #E5E5E5; font-size: 11px; color: #5C5C5C; display: flex; justify-content: space-between; }
  .sig { margin-top: 28px; display: flex; justify-content: space-between; gap: 40px; }
  .sig > div { flex: 1; border-top: 1px solid #0A0A0A; padding-top: 6px; font-size: 11px; text-align: center; }
  @media print { body { padding: 0; } button { display: none; } }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="eyebrow">Vehicle Booking Receipt</div>
      <div class="brand">${esc(dealershipName)}</div>
    </div>
    <div class="meta">
      Booking #${esc(booking.id.slice(0, 8).toUpperCase())}<br/>
      Issued ${esc(fmtDate(booking.created_at))}<br/>
      Status: ${esc((booking.status || "active").toUpperCase())}
    </div>
  </div>

  <div class="grid">
    <div class="card">
      <h3>Customer</h3>
      <div class="row"><span class="l">Name</span><span>${esc(customer.name || "—")}</span></div>
      <div class="row"><span class="l">Relation</span><span>${esc(relation)}</span></div>
      <div class="row"><span class="l">Gender</span><span>${esc(customer.gender || "—")}</span></div>
      <div class="row"><span class="l">DOB</span><span>${esc(customer.dob ? fmtDate(customer.dob) : "—")}</span></div>
      <div class="row"><span class="l">Mobile</span><span>${esc(customer.phone || "—")}</span></div>
      <div class="row"><span class="l">Alt. Mobile</span><span>${esc(customer.alternate_phone || "—")}</span></div>
      <div class="row"><span class="l">Email</span><span>${esc(customer.email || "—")}</span></div>
      <div class="row"><span class="l">Address</span><span style="text-align:right;max-width:60%">${addrLines.map(esc).join("<br/>") || "—"}</span></div>
      <div class="row"><span class="l">Aadhar</span><span>${esc(customer.aadhar_no || "—")}</span></div>
      <div class="row"><span class="l">PAN</span><span>${esc(customer.pan_no || "—")}</span></div>
      <div class="row"><span class="l">DL</span><span>${esc(customer.driving_licence_no || "—")}</span></div>
    </div>
    <div class="card">
      <h3>Vehicle</h3>
      <div class="row"><span class="l">Vehicle</span><span>${esc(vehicle.package_name || "")}</span></div>
      <div class="row"><span class="l">Variant</span><span>${esc(vehicle.variant || vehicle.trim || "—")}</span></div>
      <div class="row"><span class="l">Colour</span><span>${esc(vehicle.color || vehicle.colour || "—")}</span></div>
      <div class="row"><span class="l">VIN</span><span style="font-family:'IBM Plex Mono',monospace">${esc(vehicle.vin || "—")}</span></div>
      <div class="row"><span class="l">Type</span><span>${esc(vehicle.type || "—")}</span></div>
      <h3 style="margin-top:14px">Booking Detail</h3>
      <div class="row"><span class="l">Booking Date</span><span>${esc(fmtDate(booking.created_at))}</span></div>
      <div class="row"><span class="l">Expected Delivery</span><span>${esc(booking.expected_delivery_date ? fmtDate(booking.expected_delivery_date) : "—")}</span></div>
      <div class="row"><span class="l">Hold Until</span><span>${esc(booking.expiry_date ? fmtDate(booking.expiry_date) : "—")}</span></div>
      <div class="row"><span class="l">GST Type</span><span>${esc((booking.gst_type || "intra").toUpperCase())}</span></div>
      <div class="row"><span class="l">Booked By</span><span>${esc(booking.salesperson_name || "—")}</span></div>
    </div>
  </div>

  <div class="card">
    <h3>Payment Ledger</h3>
    <table>
      <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th>Note</th><th style="text-align:right">Amount (₹)</th></tr></thead>
      <tbody>
        ${
          payments.length === 0
            ? `<tr><td colspan="5" style="text-align:center;color:#5C5C5C;padding:14px">No payments yet.</td></tr>`
            : payments
                .map(
                  (p) => `<tr>
            <td>${esc(fmtDate(p.paid_on))}</td>
            <td style="text-transform:uppercase;font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(p.payment_method || "cash")}</td>
            <td style="font-family:'IBM Plex Mono',monospace;font-size:11px">${esc(p.reference || "—")}</td>
            <td>${esc(p.note || "—")}</td>
            <td style="text-align:right;font-weight:600">${esc(fmtMoney(p.amount))}</td>
          </tr>`,
                )
                .join("")
        }
      </tbody>
    </table>
    <div class="totals">
      <div class="row"><span class="l">Total Advance Paid</span><span>${esc(fmtMoney(totalPaid))}</span></div>
    </div>
    <div style="font-family:'IBM Plex Mono',monospace;font-size:10px;color:#666;margin-top:8px">
      Note: Final on-road price is fixed on the day of delivery as per the
      prevailing pricelist. Customer is free to switch to another available
      variant; the advance shown above will be carried over.
    </div>
  </div>

  ${
    booking.notes
      ? `<div class="card" style="margin-top:14px"><h3>Notes</h3><div style="font-size:13px;white-space:pre-wrap">${esc(booking.notes)}</div></div>`
      : ""
  }

  <div class="sig">
    <div>Customer Signature</div>
    <div>Authorised Signatory</div>
  </div>

  <div class="footer">
    <div>This is a computer-generated receipt. Subject to dealership terms.</div>
    <div>Printed ${esc(new Date().toLocaleString())}</div>
  </div>

  <script>window.addEventListener('load', () => setTimeout(() => window.print(), 200));</script>
</body></html>`;

  const w = window.open("", "_blank", "width=900,height=900");
  if (!w) {
    alert("Pop-up blocked — allow pop-ups to print receipts.");
    return;
  }
  // `document.write` is acceptable here because every interpolated value
  // flows through `esc()` above and the markup itself is a trusted constant
  // in this module. We could use srcdoc on an iframe instead, but a full new
  // window with a clean print stylesheet is what the user actually wants.
  w.document.open();
  w.document.write(html);
  w.document.close();
}
